// Modules van het bord (0029_modules; docs/DESIGN.md §8.M). Verzoek oprichter 2026-09-25:
// "Probeer alles zoveel mogelijk modulair te bouwen zodat ik het pricingmodel later per module zou
// kunnen inrichten" en "Brengen halen optie moet in de instellingen ook aangezet en uitgezet kunnen
// worden voor dat gezin, net zoals herinneringen, reistijd en locatie".
//
// Eén register, puur en zonder Zod (ook te importeren via `@amumin/domain/modules`; de kiosk
// importeert alleen types). Per module: id, naam en uitleg (nl/en), waar hij van afhangt, of hij
// standaard aan staat, of hij iets op het bord doet en een plek voor het prijsplan. De keuze per
// gezin staat in `household_module` (0029); zonder rij geldt `default_on`. Uitzetten wist nooit
// iets: weer aanzetten brengt alles terug.
import { MODULE_TEXTS } from './texts.js';
export const MODULE_IDS = [
    'rides',
    'reminders',
    'travel_time',
    'location_weather',
    // Golf 3 (0034_kaart_agenda): plaatsen zoeken bij "Waar" (Google, via onze server).
    'place_search',
    'prayer',
    // Golf 7 (0048): Soera's leren, alleen bij Islam en Koranles (docs/onderzoek/soera-leren.md).
    'memorize',
    // 0062: module Jodendom (sjabbat en joodse kalender), alleen bij Jodendom in het profiel.
    'shabbat',
    'holidays',
    'food',
    'wishes',
    'messages',
    'rooms',
    'cleaning',
    // Virtuele iPad (0028): het bord in een iPad-lijst in de ouderapp, alleen voor ouders.
    'virtual_device',
    // Meldingen op je telefoon (0038_meldingen; web push nu, App Store/Play Store later).
    'push',
    // Golf 3: Gezondheid en School (0032), Agenda koppelen (0034), Talen (0033).
    'health',
    'school',
    'calendar_sync',
    'languages',
    // Golf 6b (0042_widgets): het beginscherm Vandaag als widgets, per persoon te herschikken.
    'widgets',
    // Golf 6b (0043_huishoudtypes): Beloningen los van Taken (punten, sterren, winkel) en het
    // Beurtrooster (rouleren van klussen over huisgenoten, gebouwd op Schoonmaak).
    'rewards',
    // To-do's (0058, oprichter 1 okt: "maak van todo een aparte module en widget"): taken zonder
    // datum of tijd, het blokje onder de taken, de tegel in de plusknop en de widget.
    'todos',
    // Notities (0060, oprichter 1 okt 2026): schrijven met je vinger, typen en inspreken; widget op
    // het bord.
    'notes',
    // 0064 (oprichter 2 okt 2026): Fotolijst, foto's van het gezin als schermbeveiliging van het bord.
    'photo_frame',
    // 0068 (oprichter 3 okt 2026): Gezond op gewicht (code-naam kompas). Persoonlijk: elke volwassene
    // houdt het voor zichzelf bij in zijn eigen omgeving; niemand anders ziet het.
    'kompas',
    // 0063 (oprichter 2 okt 2026): module Hindoeïsme (neutrale id): panchang, feesten en vastendagen,
    // alleen bij hindoeïsme in 'Wat past bij ons gezin' en pas na het ja van het gezin.
    'almanac',
    'chore_rotation',
    // Golf 8 (0049_samen_wonen): Samen wonen. Allemaal standaard uit; bij studentenhuis, huisgenoten
    // en woongroep aan via household.ts, maar slapend tot het huis is ingericht (docs/onderzoek/
    // golf8-samenwonen-en-ouderen.md §2.0.2).
    'dinner_list',
    'expenses',
    'bookings',
    // Golf 8 (0050, pakket Ouderen): dagelijkse check-in en familiekring. Aan ALLEEN via een
    // expliciete keuze (household_module-rij); geen preset zet ze aan (B2).
    'check_in',
    'family_circle',
    // 0069 (oprichter 3 okt 2026): Geldzaken, privé per volwassene in zijn eigen omgeving.
    'money',
];
const t = (nl, en) => ({ nl, en });
const BASE_MODULES = [
    {
        id: 'rides',
        status: 'available',
        label: t('Brengen en halen', 'Drop-off and pick-up'),
        description: t('Wie brengt en wie haalt, per dag, met "Kan niet? Iemand anders" en de pagina Ritten.', 'Who drops off and who picks up, per day, with "Can\'t? Someone else" and the Rides page.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                key: 'show_in_agenda',
                default: true,
                label: t('Brengen en halen in de agenda tonen', 'Show drop-off and pick-up in the calendar'),
                description: t(
                // {a} en {b}: de eerste twee volwassenen van het gezin (de app vult ze in).
                'Bij elke afspraak: "{a} brengt · {b} haalt". Uit: alleen op het afspraakblad en bij Ritten.', 'On every event: "{a} drops off · {b} picks up". Off: only on the event sheet and Rides.'),
            },
            {
                key: 'travel_blocks',
                default: false,
                label: t('Reistijd als eigen blokje', 'Travel time as its own block'),
                description: t('In de dag en de week een smal blokje vóór de afspraak: wie rijdt en hoe lang.', 'In the day and week view a thin block before the event: who drives and how long.'),
                requires: 'travel_time',
            },
        ],
    },
    {
        id: 'reminders',
        status: 'available',
        label: t('Herinneringen', 'Reminders'),
        description: t('Een seintje op de tablet vóór een afspraak, zoals "Klaarmaken voor trainen", met een lijstje om in te pakken.', 'A call on the tablet before an event, such as "Get ready for practice", with a packing list.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'travel_time',
        status: 'available',
        label: t('Reistijd en vertrekken', 'Travel time and leaving'),
        description: t('Hoe lang je onderweg bent, en een seintje op de tablet als het tijd is om te gaan.', 'How long the trip takes, and a banner on the tablet when it is time to leave.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'location_weather',
        status: 'available',
        label: t('Locatie en weer', 'Location and weather'),
        description: t('Grove plaats van het gezin (nooit een adres) voor het weer en de jassen-en-laarzen-tips.', 'A rough place for the family (never an address) for the weather and coat-and-boots tips.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 3 (0034_kaart_agenda; oprichter #6 "Google Maps bedrijven zoeken bij Waar"). Hoort bij
        // de locatie: zoeken rond de grove plaats van het gezin (als die er is). Zonder deze module is
        // "Waar" gewoon een tekstveld. Hangt niet af van Locatie en weer: zonder gezinslocatie zoekt
        // hij in heel Nederland.
        id: 'place_search',
        status: 'available',
        label: t('Plaatsen zoeken', 'Place search'),
        description: t('Bij "Waar" een zwembad, school of winkel kiezen terwijl je typt (via Google Maps). Daarmee rekent de app ook de reistijd uit.', 'Pick a pool, school or shop under "Where" as you type (via Google Maps). The app then also works out the travel time.'),
        depends_on: [],
        default_on: true,
        // Op het bord (feat/bord-afspraken): zoeken bij "Waar" in het afspraakformulier. Was false,
        // waardoor de sync de module nooit noemde en het bord nooit zocht (fix/afspraakformulier).
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'prayer',
        status: 'available',
        label: t('Gebedstijden', 'Prayer times'),
        description: t('Alleen bij Islam in "Wat past bij ons gezin", en pas na het ja van het gezin.', 'Only with Islam in "What suits our family", and only after the family says yes.'),
        depends_on: ['location_weather'],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                // Golf 4 (DESIGN §8.GB.14): tijdens de Ramadan imsak en iftar groot op het bord.
                key: 'ramadan',
                default: true,
                label: t('Ramadan op het bord', 'Ramadan on the board'),
                description: t('Tijdens de Ramadan het begin en het einde van het vasten groot op het bord, met een rustige aftelling in het laatste uur. Geen eigen geluid.', 'During Ramadan the start and end of the fast large on the board, with a calm countdown in the last hour. No sound of its own.'),
            },
            {
                // Koran luisteren (oprichter 1 okt 2026): standaard speelt wat iemand startte gewoon door.
                key: 'quran_quiet',
                default: false,
                label: t('Koran stopt in de stille uren', 'Quran stops in quiet hours'),
                description: t('Speelt er een recitatie op het bord, dan stopt die als de stille uren of de nacht beginnen. Uit: wat iemand startte, speelt door.', 'If a recitation is playing on the board, it stops when quiet hours or the night begin. Off: what someone started keeps playing.'),
            },
        ],
    },
    {
        // Golf 7 (0048, docs/onderzoek/soera-leren.md §4.3): alleen aangeboden bij Islam én Koranles in
        // "Wat past bij ons gezin" (de API laat de regel anders weg, net als Gebedstijden). Aan staat
        // hij pas na het ja van het gezin en per kind (memorize_settings, consent_record).
        id: 'memorize',
        status: 'available',
        label: t("Soera's leren", 'Learning surahs'),
        description: t("Alleen bij Islam en Koranles in 'Wat past bij ons gezin', en pas na jullie ja per kind.", "Only with Islam and Quran lessons in 'What suits our family', and only after your yes for each child."),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // 0062 (oprichter 2 okt 2026): alleen aangeboden bij Jodendom in 'Wat past bij ons gezin' (de
        // API laat de regel anders weg, net als Gebedstijden). Aan staat hij pas na het ja van het gezin
        // (judaism_settings.enabled) en met de profieltoestemming.
        id: 'shabbat',
        status: 'available',
        label: t('Sjabbat en joodse kalender', 'Shabbat and Jewish calendar'),
        description: t("Alleen bij Jodendom in 'Wat past bij ons gezin', en pas na het ja van het gezin.", "Only with Judaism in 'What suits our family', and only after the family says yes."),
        depends_on: ['location_weather'],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'holidays',
        status: 'available',
        label: t('Bijzondere dagen', 'Special days'),
        description: t(
        // De zin over Suikerfeest en Offerfeest voegt de app alleen toe bij de islamitische set.
        'Nederlandse feestdagen, schoolvakanties en de bijzondere dagen die jullie kozen. Verjaardagen blijven.', 'Dutch public holidays, school holidays and the special days you picked. Birthdays stay.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'food',
        status: 'available',
        label: t('Eten', 'Food'),
        description: t('Boodschappen, recepten, weekmenu en voorraad.', 'Groceries, recipes, meal plan and pantry.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'wishes',
        status: 'available',
        label: t('Wensen', 'Wishes'),
        description: t('Verlanglijstjes van de kinderen, ook te delen met familie.', "The children's wish lists, also to share with family."),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'messages',
        status: 'available',
        label: t('Berichten', 'Messages'),
        description: t('Korte berichtjes en spraakberichten tussen het bord, de ouders en familie.', 'Short notes and voice messages between the board, the parents and family.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'rooms',
        status: 'available',
        label: t('Kamers en bewoners', 'Rooms and residents'),
        description: t('Een tablet in een slaapkamer toont alleen wie daar slaapt. Uit: elke tablet toont het hele gezin.', 'A tablet in a bedroom shows only who sleeps there. Off: every tablet shows the whole family.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'cleaning',
        status: 'available',
        label: t('Schoonmaak', 'Cleaning'),
        description: t('De schoonmaakkast: wie wat schoonmaakte, en wat weer aan de beurt is.', 'The cleaning cupboard: who cleaned what, and what is due again.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // 0028: het ECHTE bord (kiosk-origin) in een iPad-lijst; koppelt als eigen apparaat. Uit: het
        // scherm en /v1/app/virtual-device zijn weg; een al gekoppelde virtuele iPad blijft in Tablets
        // staan (intrekken kan daar), er wordt niets gewist.
        id: 'virtual_device',
        status: 'available',
        label: t('Virtuele iPad', 'Virtual iPad'),
        description: t('Het bord van de tablet in je browser, bijvoorbeeld op je laptop. Alleen voor ouders.', 'The tablet board in your browser, for example on your laptop. Parents only.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: false,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'push',
        status: 'available',
        label: t('Meldingen', 'Notifications'),
        description: t('Seintjes op je telefoon: herinneringen, ritten, berichten en wat op je ja wacht. Ieder kiest zelf wat hij krijgt.', 'Notifications on your phone: reminders, rides, messages and what waits for your yes. Everyone picks what they get.'),
        depends_on: [],
        default_on: true,
        // De tablet krijgt hier niets van (geen scherm, geen sync-veld).
        kiosk_visible: false,
        min_plan: 'free',
        options: [],
    },
    // 0032 (golf 3): gezondheidsgegevens (AVG art. 9). Standaard UIT en nooit op de tablet; per lid
    // pas na de uitdrukkelijke toestemming van een ouder (docs/PRIVACY-groei-school.md).
    {
        id: 'health',
        status: 'available',
        label: t('Gezondheid', 'Health'),
        description: t('Lengte en gewicht per kind in een groeicurve. Alleen voor ouders, na jullie toestemming. Niet op de tablet, tenzij een ouder dat zelf voor één tablet bevestigt.', 'Height and weight per child on a growth chart. Parents only, after your consent. Not on the tablet, unless a parent confirms it for one tablet themselves.'),
        depends_on: [],
        default_on: false,
        kiosk_visible: false,
        min_plan: 'free',
        options: [],
    },
    // 0032 (golf 3): rapportcijfers en toetsen van de basisschool. Standaard UIT, nooit op de tablet.
    {
        id: 'school',
        status: 'available',
        label: t('School', 'School'),
        description: t('Huiswerk, vaste huiswerktijden en toetsen met oefenmomenten per kind, ook voor lessen zoals muziekles of bijles. Cijfers, rapportcijfers, Cito- en AVI-niveaus, doorstroomtoets en schooladvies. Huiswerk en toetsen kunnen op het bord; cijfers zien alleen ouders.', 'Homework, regular homework times and tests with practice sessions per child, also for lessons such as music or tutoring. Grades, report grades, Cito and AVI levels, the year-8 test and school advice. Homework and tests can go on the board; only parents see grades.'),
        depends_on: [],
        default_on: false,
        // 0076: de planner (huiswerk en toetsen, nooit een cijfer) kan als widget op het bord.
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                // 0076 (privacyreview S2): huiswerk en oefenen staan als taak op het bord, waar bezoek
                // meekijkt. Standaard heten die taken neutraal ("Huiswerk", "Oefenen voor een toets"); met
                // deze keuze staan vak, les en onderwerp erbij. Geldt voor wat je daarna toevoegt of wijzigt.
                key: 'board_details',
                default: false,
                label: t('Vak en onderwerp in de taken op het bord', 'Subject and topic in the tasks on the board'),
                description: t('Standaard staat er op de takenlijst alleen "Huiswerk" of "Oefenen voor een toets"; de details staan in de app en in de widget School. Aan: ook vak, les en onderwerp (bijvoorbeeld "Oefenen Rekenen: Breuken"). Geldt voor wat je daarna toevoegt of wijzigt.', 'By default the task list only says "Homework" or "Practise for a test"; the details are in the app and in the School widget. On: also subject, lesson and topic (for example "Practise Maths: Fractions"). Applies to what you add or change afterwards.'),
            },
        ],
    },
    {
        // Golf 3 (0034_kaart_agenda; oprichter #14 "pushen naar Gmail, O365, iCloud", antwoord "Eerst
        // een richting"): een abonnementslink (iCalendar) per gezin of per lid, alleen lezen.
        id: 'calendar_sync',
        status: 'available',
        label: t('Agenda koppelen', 'Calendar sync'),
        description: t('Jullie afspraken ook in Google Agenda, Outlook of op de iPhone, via een eigen link. Eén kant op: wat je daar verandert, komt niet terug.', 'Your events also in Google Calendar, Outlook or on the iPhone, through a private link. One way: changes made there do not come back.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: false,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 3 (0033_talen). De taal van het gezin kiest elk gezin altijd (onboarding, Instellingen);
        // deze module is het extra: een eigen taal per gezinslid en per tablet. Uit: iedereen volgt het
        // gezin; de keuzes blijven bewaard en tellen weer zodra hij aan gaat.
        id: 'languages',
        status: 'available',
        label: t('Talen per persoon en tablet', 'Languages per person and tablet'),
        description: t('Iedereen zijn eigen taal: een ouder kiest per gezinslid, en een tablet in een kamer kan een andere taal spreken dan het gezin.', 'Everyone their own language: a parent picks one per family member, and a tablet in a room can speak another language than the family.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: false,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 6b (0042_widgets; oprichter 2026-09-26 "het beginscherm kunnen herschikken als zijnde
        // widgets"). Uit: iedereen ziet de standaardindeling en de knop Aanpassen is weg; de gekozen
        // indelingen blijven bewaard en tellen weer zodra hij aan gaat.
        id: 'widgets',
        status: 'available',
        label: t('Beginscherm op maat', 'Custom home screen'),
        description: t('Iedereen kiest zelf wat er op Vandaag staat en in welke volgorde, zoals het weer, de boodschappen of de berichten.', 'Everyone picks what is on Today and in which order, such as the weather, the shopping list or messages.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: false,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 6b (0043_huishoudtypes): punten, sterren, doelen en de beloningswinkel, los van Taken.
        // Standaard aan (bestaande gezinnen verliezen niets); bij stel, alleen, huisgenoten en care
        // verborgen (household.ts). Uit: geen punten erbij, geen sterren, geen winkel, ook niet op de
        // tablet; het saldo, de beloningen en de aanvragen blijven bewaard tot hij weer aan gaat.
        id: 'rewards',
        status: 'available',
        label: t('Beloningen', 'Rewards'),
        description: t('Punten en sterren voor taken en schoonmaken, een doel en de beloningswinkel op de tablet.', 'Points and stars for tasks and cleaning, a goal and the rewards shop on the tablet.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // To-do's (0058). Standaard aan, ook voor bestaande gezinnen: wie al to-do's heeft, verliest
        // niets, en een to-do is net zo algemeen als een taak (elke soort huishouden). Uit: geen tab en
        // geen keuze in de ouderapp, geen blokje onder de taken, geen tegel in de plusknop en geen
        // widget; de API maakt geen to-do's meer (module_off). Alles blijft bewaard.
        id: 'todos',
        status: 'available',
        label: t("To-do's", 'To-dos'),
        description: t('Klussen zonder datum of tijd, zoals "schoenenkast verven". Ze blijven staan tot ze af zijn, op het bord onder de taken en als widget.', 'Jobs without a date or time, like "paint the shoe cupboard". They stay until done, on the board under the tasks and as a widget.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Notities (0060_notes; oprichter 1 okt 2026: "notities waar je op kan schrijven met je
        // vinger, kan typen en kan inspreken"). Standaard aan: een briefje op de gezinsmuur past bij
        // elke soort huishouden. Uit: geen widget en geen notities op het bord, geen pagina in de app;
        // de API geeft 404 module_off. Alles blijft bewaard.
        id: 'notes',
        status: 'available',
        label: t('Notities', 'Notes'),
        description: t('Briefjes op het bord: schrijf met je vinger, typ of spreek iets in. Voor iedereen of voor één persoon, met een kleur en vastgeprikt als het belangrijk is.', 'Notes on the board: write with your finger, type or say something. For everyone or for one person, with a colour, and pinned when it matters.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                // Privacy (AVG; CLAUDE.md "geen externe verzoeken"): de spraakherkenning van Chrome stuurt
                // het geluid naar Google, behalve als het toestel het zelf kan (dan altijd zonder deze
                // keuze). Standaard uit; alleen ouders zetten hem aan (module.update).
                key: 'cloud_speech',
                default: false,
                label: t('Spraak naar tekst via Google (audio gaat naar Google)', 'Speech to text via Google (audio goes to Google)'),
                description: t('Kan het toestel spraak niet zelf omzetten, dan stuurt de browser je stem naar Google om er tekst van te maken. Zonder deze keuze bewaren we een opname bij de notitie, zonder tekst.', 'If the device cannot turn speech into text by itself, the browser sends your voice to Google to make text of it. Without this choice we keep a recording with the note, without text.'),
            },
        ],
    },
    {
        // Fotolijst (0064_photo_frame; oprichter 2 okt 2026: "ik wil dat je de optie geeft aan mensen
        // om te kiezen om hun schermbeveiliging een fotolijst te maken die automatisch de hele lijst
        // doorloopt"). Standaard uit: zonder foto's doet hij niets. Alleen ouders zien de foto's en de
        // pagina (parent_only); het bord toont ze als schermbeveiliging (keuze per bord bij Tablets).
        // Uit: geen blok in de sync (dus de bekende schermbeveiliging), geen pagina; de API geeft 404
        // module_off. De foto's blijven bewaard tot een ouder ze wist.
        id: 'photo_frame',
        status: 'available',
        label: t('Fotolijst', 'Photo frame'),
        description: t("Jullie eigen foto's als schermbeveiliging van het bord: alleen de foto's, of een foto met de klok, de datum en een paar widgets. De foto's blijven privé op jullie eigen bord.", "Your own photos as the board's screensaver: just the photos, or a photo with the clock, the date and a few widgets. The photos stay private on your own board."),
        depends_on: [],
        default_on: false,
        kiosk_visible: true,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Gezond op gewicht (0068_kompas; oprichter 3 okt 2026: "ik wil afvallen ... het moet mij niet
        // demotiveren"). Standaard uit. Aan betekent alleen dat de volwassenen van dit huishouden hem
        // kunnen gebruiken: ieder geeft zelf toestemming (AVG art. 9) en alles staat in zijn eigen
        // persoonlijke omgeving (0053), nooit zichtbaar voor de andere ouders of het bord. Alleen als
        // iemand dat zelf kiest, komt er een kleine tegel op het bord (water, beweging, reeks).
        // Uit: geen pagina en geen tegel; de API geeft 404 module_off. De gegevens blijven tot de
        // persoon ze wist of 365 dagen niets doet.
        id: 'kompas',
        status: 'available',
        label: t('Gezond op gewicht', 'Healthy weight'),
        description: t('Rustig bijhouden wat je eet, drinkt en beweegt, met een veilig dagdoel en mooie grafieken. Persoonlijk: elke volwassene houdt het voor zichzelf bij en niemand anders ziet het.', 'Calmly keep track of what you eat, drink and move, with a safe daily target and clear charts. Personal: each adult keeps it for themselves and nobody else sees it.'),
        depends_on: [],
        default_on: false,
        kiosk_visible: true,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        // 0063: module Hindoeïsme. Alleen aangeboden bij hindoeïsme bij het gezin of een lid (de API laat
        // de regel anders weg, net als Gebedstijden); aan pas na het ja van het gezin (hindu_settings).
        id: 'almanac',
        status: 'available',
        label: t('Hindoeïsme', 'Hinduism'),
        description: t('Panchang, feesten en vastendagen op het bord. Alleen bij hindoeïsme in "Wat past bij ons gezin", en pas na het ja van het gezin.', 'Panchang, festivals and fast days on the board. Only with Hinduism in "What suits our family", and only after the family says yes.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 6b: het beurtrooster (docs/onderzoek/feedback-26-sep.md §3.3). Klussen uit Schoonmaak
        // rouleren per week (of per dag, zoals koken) over wie meedoet, met ruilen en afwezig.
        // Standaard uit; bij huisgenoten aan (household.ts).
        id: 'chore_rotation',
        status: 'available',
        label: t('Beurtrooster', 'Chore rotation'),
        description: t('Keuken, badkamer, gang, vuilnis en afwas rouleren elke week eerlijk over iedereen, met ruilen en afwezig. Het bord in de gang toont wie aan de beurt is.', 'Kitchen, bathroom, hallway, bins and dishes rotate fairly every week, with swaps and time away. The hallway board shows whose turn it is.'),
        depends_on: ['cleaning'],
        default_on: false,
        kiosk_visible: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 8 (0049): Wie eet mee. Hangt af van Eten (de keuken hoort erbij). Op het bord in de gang
        // (optie on_board) en boodschappen verdelen over de eters (optie dinner_costs, met Kosten delen).
        id: 'dinner_list',
        status: 'available',
        label: t('Wie eet mee', "Who's eating"),
        description: t('Vóór de deadline aan- of afmelden, wie kookt er, en met hoeveel eten we.', 'Sign up or out before the cut-off, who cooks, and how many are eating.'),
        depends_on: ['food'],
        default_on: false,
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                key: 'on_board',
                default: true,
                label: t('Op het bord in de gang', 'On the hallway board'),
                description: t('Vanavond eten en de kok op het bord, met aan- en afmelden via de naam.', 'Dinner tonight and the cook on the board, with signing up by name.'),
            },
            {
                key: 'dinner_costs',
                default: true,
                label: t('Boodschappen verdelen over de eters', 'Split the groceries over the eaters'),
                description: t('De kok voert de boodschappen in; die worden verdeeld over wie meeat (gasten tellen bij wie ze meebracht).', 'The cook enters the groceries; they are split over who ate (guests count for whoever brought them).'),
                requires: 'expenses',
            },
        ],
    },
    {
        // Golf 8 (0049): Kosten delen. Alleen volwassenen (parent_only); nooit op het bord, in de sync
        // of in een melding. Er gaat geen geld via AMUMIN.
        id: 'expenses',
        status: 'available',
        label: t('Kosten delen', 'Split costs'),
        description: t('Wie betaalde wat, en het saldo. Alle volwassenen in huis zien het. Er gaat geen geld via AMUMIN.', 'Who paid what, and the balances. All adults in the household can see it. No money goes through AMUMIN.'),
        depends_on: [],
        default_on: false,
        kiosk_visible: false,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Golf 8 (0049): Reserveren (wasmachine, droger, gemeenschappelijke ruimte, logeerbed).
        id: 'bookings',
        status: 'available',
        label: t('Reserveren', 'Bookings'),
        description: t('De wasmachine, droger, gemeenschappelijke ruimte of het logeerbed, met huisregels.', 'The washing machine, dryer, common room or guest bed, with house rules.'),
        depends_on: [],
        default_on: false,
        kiosk_visible: true,
        min_plan: 'free',
        options: [
            {
                key: 'on_board',
                default: true,
                label: t('Op het bord in de gang', 'On the hallway board'),
                description: t('Per ding "nu vrij" of wie het heeft, en reserveren via de naam.', 'For each thing "free now" or who has it, and booking by name.'),
            },
        ],
    },
    {
        // Golf 8 (0050; docs/onderzoek/golf8-samenwonen-en-ouderen.md §3). Geen alarmdienst: geen
        // valdetectie, geen 112-knop. Zonder meldingen geen seintje, dus hangt af van push.
        id: 'check_in',
        status: 'available',
        label: t('Dagelijkse check-in', 'Daily check-in'),
        description: t("Eén grote knop 'Alles goed' op het bord. Familie krijgt een seintje als die uitblijft. Geen alarmdienst.", "One big 'All good' button on the board. Family is notified when it is missing. Not an alarm service."),
        depends_on: ['push'],
        default_on: false,
        kiosk_visible: true,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        id: 'family_circle',
        status: 'available',
        label: t('Familiekring', 'Family circle'),
        description: t('Familie op afstand kijkt mee, met rechten per persoon. De oudere zegt zelf ja.', 'Family at a distance can follow along, with rights per person. The older person says yes themselves.'),
        depends_on: [],
        default_on: false,
        kiosk_visible: true,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
    {
        // Geldzaken (0069_geldzaken; oprichter 3 okt 2026). Privé per volwassene: alles staat in zijn
        // EIGEN omgeving (0053), nooit in het gezin, nooit op het bord, nooit bij Beheer. Standaard aan:
        // in een gezin is het alleen de ingang (Geldzaken opent je eigen omgeving), daar zie je alleen
        // je eigen geld. Uit: geen ingang en geen pagina; de API geeft 404 module_off. Niets wordt gewist.
        // Geen beleggingsadvies (Wft): inzicht, uitleg en een planner waarin je zelf alles kiest.
        id: 'money',
        status: 'available',
        label: t('Geldzaken', 'Money'),
        description: t('Je eigen bankafschrift inlezen: vaste lasten, waar je geld heen gaat, waar je kunt besparen en een buffer- en verdeelplanner. Privé: alleen jij ziet het, in je eigen omgeving, tenzij je zelf iets voor één tablet bevestigt. Geen financieel advies.', 'Read in your own bank statement: fixed costs, where your money goes, where you can save, and a buffer and split planner. Private: only you see it, in your own space, unless you confirm something for one tablet yourself. Not financial advice.'),
        depends_on: [],
        default_on: true,
        kiosk_visible: false,
        parent_only: true,
        min_plan: 'free',
        options: [],
    },
];
/** Vult tr/ar/de/fr uit texts.ts aan (nl en en staan hierboven bij de module zelf). */
function withTexts(def) {
    const own = MODULE_TEXTS[def.id];
    if (!own)
        return def;
    return {
        ...def,
        label: { ...own.label, ...def.label },
        description: { ...own.description, ...def.description },
        options: def.options.map((o) => {
            const opt = own.options?.[o.key];
            return opt
                ? {
                    ...o,
                    label: { ...opt.label, ...o.label },
                    description: { ...opt.description, ...o.description },
                }
                : o;
        }),
    };
}
export const MODULES = BASE_MODULES.map(withTexts);
const BY_ID = new Map(MODULES.map((m) => [m.id, m]));
export function isModuleId(value) {
    return typeof value === 'string' && BY_ID.has(value);
}
export function moduleDef(id) {
    const def = BY_ID.get(id);
    if (!def)
        throw new TypeError(`onbekende module ${id}`);
    return def;
}
/** Modules die een ouder kan kiezen (in registervolgorde). */
export function availableModules() {
    return MODULES.filter((m) => m.status === 'available');
}
/** Modules die van `id` afhangen (direct): die gaan mee uit als `id` uitgaat. */
export function dependentsOf(id) {
    return MODULES.filter((m) => m.depends_on.indexOf(id) >= 0).map((m) => m.id);
}
/** Naam in de taal van het huishouden (valt terug op Nederlands). */
export function moduleText(text, locale) {
    const lang = locale ? locale.slice(0, 2).toLowerCase() : 'nl';
    if (lang === 'en')
        return text.en;
    const own = lang === 'tr'
        ? text.tr
        : lang === 'ar'
            ? text.ar
            : lang === 'de'
                ? text.de
                : lang === 'fr'
                    ? text.fr
                    : undefined;
    return own && own.length > 0 ? own : text.nl;
}
//# sourceMappingURL=registry.js.map